@if (experts.isLoading() || internalUsers.isLoading() || segments.isLoading()) {
  <div class="flex justify-center items-center h-32">
    <mat-progress-spinner diameter="32" mode="indeterminate" />
  </div>
} @else if ((experts.value() ?? []).length === 0) {
  <p class="text-sm text-gray-500">No MVP experts found for this opportunity</p>
} @else {
  <app-mvp-experts-filter
    [experts]="experts.value() ?? []"
    [internalUsers]="internalUsers.value() ?? {}"
    [segments]="segments.value() ?? {}"
    [projectType]="opportunity().projectType"
    (filterChange)="filter.set($event)"
  />

  @if (filteredExperts().length === 0) {
    <p class="text-sm text-gray-500">No experts match your filters</p>
  } @else {
    <ul class="flex flex-col gap-3">
      @for (expert of filteredExperts(); track expert.opportunityExpertId) {
        <li class="flex min-h-24 items-center gap-8 rounded-lg border bg-white p-2 shadow-sm">
          <app-initials-avatar [firstName]="expert.firstName" [lastName]="expert.lastName" />

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <a
                class="text-blue-600 hover:underline font-medium text-sm"
                [routerLink]="['/', 'admin', 'experts', expert.opportunityExpertId]"
              >
                @if (expert.firstName || expert.lastName) {
                  {{ expert.firstName || '' }} {{ expert.lastName || '' }}
                } @else {
                  Missing name
                }

                @if (expert.firstNameAlias || expert.lastNameAlias) {
                  <span class="text-xs text-gray-500">
                    ({{ expert.firstNameAlias || '' }} {{ expert.lastNameAlias || '' }})
                  </span>
                }
              </a>

              @for (type of expert.mvpTypes; track type) {
                <span
                  class="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600"
                  [matTooltip]="type | mvpExpertTypeTooltip"
                >
                  {{ type | mvpExpertType }}
                </span>
              }

              <span
                class="text-xs px-2 py-0.5 rounded-full"
                [class.bg-red-50]="expert.progression === 'rejected'"
                [class.text-red-600]="expert.progression === 'rejected'"
                [class.bg-violet-50]="expert.progression !== 'rejected'"
                [class.text-violet-600]="expert.progression !== 'rejected'"
              >
                Status: {{ expert.progression | titlecase }}
              </span>

              @if (expert.attemptedOutreachTypes?.length) {
                <span class="flex items-center gap-1">
                  @for (outreach of expert.attemptedOutreachTypes; track outreach) {
                    @if (outreach === 'call') {
                      <button
                        type="button"
                        class="flex items-center"
                        matTooltip="Call outreach attempted - click to view"
                        aria-label="View call solicitations"
                        (click)="openCallSolicitationsDialog(expert)"
                      >
                        <mat-icon class="!text-base text-blue-500">phone</mat-icon>
                      </button>
                    } @else {
                      <mat-icon
                        class="!text-base text-gray-400"
                        [matTooltip]="outreach === 'email' ? 'Email outreach attempted' : 'SMS outreach attempted'"
                      >
                        {{ outreach === 'email' ? 'email' : 'sms' }}
                      </mat-icon>
                    }
                  }
                </span>
              }
            </div>

            @if (expert.jobHistory?.[0]; as latestJob) {
              <p class="text-sm text-gray-700 mt-0.5">
                {{ latestJob.jobTitle }}
                &middot;
                {{ latestJob.company }}
                &middot;
                {{ latestJob.startDate | date: 'MMM yyyy' }}
                &ndash;
                @if (latestJob.endDate) {
                  {{ latestJob.endDate | date: 'MMM yyyy' }}
                } @else {
                  Current
                }
              </p>
            }

            <div class="flex flex-wrap gap-x-4 gap-y-1 mt-1">
              @if (expert.country) {
                <span class="text-xs text-gray-500">Country: {{ expert.country }}</span>
              }

              @if (internalUsers.value()?.[expert.ownerConnectId]; as owner) {
                <span class="text-xs text-gray-500" [matTooltip]="owner.email"
                  >Owner: {{ owner.first_name }} {{ owner.last_name }}</span
                >
              } @else {
                <span class="text-xs text-gray-500">Unknown owner: {{ expert.ownerConnectId }}</span>
              }

              @if (segments.value()?.[expert.opportunitySegmentId]; as segment) {
                <span
                  class="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700"
                  [matTooltip]="expert.opportunitySegmentId"
                  >Segment: {{ segment.geography }} &middot; {{ segment.segment }}</span
                >
              } @else {
                <span class="text-xs text-gray-500">Unknown segment: {{ expert.opportunitySegmentId }}</span>
              }

              @if (expert.expertSourceName) {
                <span class="text-xs text-gray-500">Search: {{ expert.expertSourceName }}</span>
              }
            </div>

            @if (expert.honoraria && (expert.honoraria?.qual || expert.honoraria?.quant)) {
              <div class="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                <span class="text-xs text-gray-500 font-medium"> Honoraria ({{ expert.honoraria.currency }}) </span>
                @if (expert.honoraria.qual !== null && expert.honoraria.qual !== undefined) {
                  <span class="text-xs text-gray-500">
                    Qual: {{ expert.honoraria.qual | currency: expert.honoraria.currency }} per hour
                  </span>
                }
                @if (expert.honoraria.quant !== null && expert.honoraria.quant !== undefined) {
                  <span class="text-xs text-gray-500">
                    Quant: {{ expert.honoraria.quant | currency: expert.honoraria.currency }} per survey
                  </span>
                }
              </div>
            }
          </div>

          <div class="flex flex-col justify-center gap-2 w-72 shrink-0">
            @if (expert.primaryEmail) {
              <div class="flex items-center gap-1">
                <button
                  mat-icon-button
                  type="button"
                  aria-label="Send email"
                  data-test-id="mvp-experts-list-send-email-button"
                  matTooltip="Send email"
                  (click)="openEmailDialog(expert)"
                >
                  <mat-icon>email</mat-icon>
                </button>
                <span class="text-xs text-gray-500 truncate" [matTooltip]="expert.primaryEmail">{{
                  expert.primaryEmail
                }}</span>
              </div>
            }
            @if (expert.primaryPhoneNumber) {
              <div class="flex items-center gap-1">
                <button
                  mat-icon-button
                  type="button"
                  aria-label="Call expert"
                  data-test-id="mvp-experts-list-call-button"
                  matTooltip="Call expert"
                  (click)="openCallDialog(expert)"
                >
                  <mat-icon>phone</mat-icon>
                </button>
                <button
                  type="button"
                  class="min-w-0 truncate text-left text-xs text-blue-600 hover:underline"
                  data-test-id="mvp-experts-list-phone-number"
                  [matTooltip]="expert.primaryPhoneNumber"
                  (click)="openCallDialog(expert)"
                >
                  {{ expert.primaryPhoneNumber }}
                </button>
              </div>
            }
            @if (expert.linkedin) {
              <a
                class="flex min-w-0 items-center gap-1 text-xs text-blue-600 hover:underline"
                aria-label="Open LinkedIn profile"
                target="_blank"
                rel="noopener noreferrer"
                [matTooltip]="expert.linkedin"
                [href]="expert.linkedin"
              >
                <mat-icon class="!text-base shrink-0">link</mat-icon>
                <span class="truncate">{{ expert.linkedin }}</span>
              </a>
            }
          </div>

          <div class="grid h-20 w-20 shrink-0 grid-cols-2 grid-rows-2 place-items-center">
            @if (expert.pinnedNotes.length) {
              <button
                mat-icon-button
                type="button"
                class="col-start-1 row-start-1 row-span-2 !h-10 !w-10 !p-0"
                aria-label="View pinned profile notes"
                matTooltip="View pinned profile notes"
                data-test-id="mvp-experts-list-profile-notes-button"
                (click)="openProfileNotesDialog(expert)"
              >
                <mat-icon>sticky_note_2</mat-icon>
              </button>
            } @else {
              <span class="col-start-1 row-start-1 row-span-2 h-10 w-10" aria-hidden="true"></span>
            }
            <a
              mat-icon-button
              class="col-start-2 row-start-1 !h-10 !w-10 !p-0"
              matTooltip="Open expert page"
              [routerLink]="['/', 'admin', 'experts', expert.opportunityExpertId]"
            >
              <mat-icon>work_outline</mat-icon>
            </a>
            <a
              mat-icon-button
              class="col-start-2 row-start-2 !h-10 !w-10 !p-0"
              matTooltip="Open profile"
              [routerLink]="['/', 'admin', 'profiles', expert.profileId]"
            >
              <mat-icon>person</mat-icon>
            </a>
          </div>
        </li>
      }
    </ul>
  }
}
